<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <page-header-new
      :title="$t('Channels_EmailSettings')"
      :breadcrumbs="breadcrumbs"
    />
    <page-content>
      <rest-form
        ref="form"
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        :model="model"
        :disabled="isDisabled"
      >
        <form-section>
          <el-form :model="model" :disabled="isDisabled">
            <el-row>
              <el-col :span="9">
                <form-section-header
                  :title="$t('EmailSettings_Settings_Title')"
                  :description="$t('EmailSettings_Settings_Description')"
                >
                  <template slot="controls">
                    <save-button :click="save" />
                  </template>
                </form-section-header>

                <form-item
                  class="margin-right"
                  prop="email_subscription_logo_file"
                  :label="$t('EmailSettings_Logo_Title')"
                >
                  <icon-upload
                    :value="model.email_subscription_page.logo"
                    :file.sync="model.email_subscription_logo_file"
                    accepts=".gif,.jpg,.jpeg,.png,.ico"
                    size="medium"
                  />
                </form-item>

                <div class="page-styles">
                  <color-picker-field
                    v-model="model.email_subscription_page.background"
                    label-text="Background color"
                  ></color-picker-field>

                  <color-picker-field
                    v-model="model.email_subscription_page.highlight"
                    label-text="Highlight color"
                  ></color-picker-field>
                </div>

                <rest-form-item
                  prop="email_subscription_page.email_change_enabled"
                  :label="$t('EmailSettings_AllowChangeEmail_Title')"
                  :description="$t('EmailSettings_PageConfig_Desc')"
                >
                  <toggle
                    v-model="model.email_subscription_page.email_change_enabled"
                  />
                </rest-form-item>

                <rest-form-item
                  prop="email_subscription_page.manage_preferences_enabled"
                  :label="$t('EmailSettings_AllowManagePreferences_Title')"
                  :description="$t('EmailSettings_AllowManagePreferences_Desc')"
                >
                  <toggle
                    v-if="isSubscriptionPreferencesEnabled"
                    v-model="
                      model.email_subscription_page.manage_preferences_enabled
                    "
                    @change="onManagePreferencesChange"
                  />
                  <toggle
                    v-else
                    :disabled="true"
                    :text="
                      $t('EmailSettings_AllowManagePreferences_NotEnabled')
                    "
                  />
                </rest-form-item>
                <rest-form-item
                  v-if="
                    isReleaseSubscriptionPreferencesDisableGlobalUnsubscribeEnabled
                  "
                  prop="subscription_preferences_global_unsubscribe_from_all_enabled"
                  :label="$t('EmailSettings_Global_Unsubscribe_Options')"
                  :description="
                    $t('EmailSettings_Global_Unsubscribe_Options_Description')
                  "
                >
                  <toggle
                    :disabled="
                      !model.email_subscription_page.manage_preferences_enabled
                    "
                    v-model="
                      model.subscription_preferences_global_unsubscribe_from_all_enabled
                    "
                  />
                </rest-form-item>
              </el-col>

              <el-col :span="15">
                <unsubscribe-preview
                  :logo-url="logo"
                  :can-manage-subscription="1"
                  :allow-change-field="
                    model.email_subscription_page.email_change_enabled
                  "
                  :background-color="model.email_subscription_page.background"
                />
              </el-col>
            </el-row>
          </el-form>
        </form-section>

        <form-section
          v-if="isEmailSettingsVisible"
          :title="$t('EmailSettings_EmailSettings_Title')"
          :description="$t('EmailSettings_EmailSettings_Description')"
        >
          <template slot="controls">
            <el-button
              type="primary"
              plain
              @click="emailVerify"
              :loading="verifyLoading"
              :disabled="!needVerification"
            >
              {{
                needVerification
                  ? $t('EmailSettings_EmailVerification_Approve')
                  : $t('EmailSettings_EmailVerification_Done')
              }}
            </el-button>
            <save-button :click="save" />
          </template>

          <el-form
            ref="settings"
            :model="model"
            :disabled="isEmailSettingsDisabled"
            :rules="validationRules"
          >
            <el-row>
              <el-col :span="8">
                <form-item
                  prop="email_type"
                  :label="$t('EmailSettings_EmailBuilder')"
                >
                  <radio-group v-model="model.email_type">
                    <radio
                      :label="1"
                      :title="$t('EmailSettings_Option_BeePlugin')"
                      :description="$t('EmailSettings_Option_BeePlugin_Desc')"
                    />
                    <radio
                      :label="0"
                      :title="$t('EmailSettings_Option_Internal')"
                      :description="$t('EmailSettings_Option_Internal_Desc')"
                    />
                  </radio-group>
                </form-item>
              </el-col>

              <el-col :span="8">
                <form-item
                  prop="email_gateway"
                  :label="$t('EmailSettings_Gateway')"
                >
                  <radio-group v-model="model.email_gateway">
                    <radio
                      label="pmta"
                      :title="$t('EmailSettings_Option_Pmta')"
                      :description="$t('EmailSettings_Option_Pmta_Desc')"
                    />
                    <radio
                      label="pmtaapi"
                      :title="$t('EmailSettings_Option_PmtaApi')"
                      :description="$t('EmailSettings_Option_PmtaApi_Desc')"
                    />
                    <radio
                      label="ses"
                      :title="$t('EmailSettings_Option_Ses')"
                      :description="$t('EmailSettings_Option_Ses_Desc')"
                    />
                  </radio-group>
                </form-item>
              </el-col>

              <el-col :span="16" class="m-top-30">
                <form-item
                  prop="email_mta_queue"
                  :label="$t('EmailSettings_MtaQueue')"
                >
                  <el-input v-model="model.email_mta_queue" />
                </form-item>

                <form-item
                  prop="email_return_domain"
                  :label="$t('EmailSettings_ReturnDomain')"
                >
                  <el-input v-model="model.email_return_domain" />
                </form-item>

                <form-item
                  prop="email_vanity_domain"
                  :label="$t('EmailSettings_VanityDomain')"
                >
                  <el-input v-model="model.email_vanity_domain" />
                </form-item>
              </el-col>
              <el-col
                v-if="model.email_gateway === 'pmtaapi'"
                :span="16"
                class="m-top-30"
              >
                <form-item
                  prop="email_gateway_params.host"
                  :label="$t('EmailSettings_PmtaHost')"
                >
                  <el-input v-model="model.email_gateway_params.host" />
                </form-item>
                <form-item
                  prop="email_gateway_params.port"
                  :label="$t('EmailSettings_PmtaPort')"
                >
                  <el-input v-model.number="model.email_gateway_params.port" />
                </form-item>
                <form-item
                  prop="email_gateway_params.baseuri"
                  :label="$t('EmailSettings_PmtaBaseuri')"
                >
                  <el-input v-model="model.email_gateway_params.baseuri" />
                </form-item>
                <form-item prop="email_gateway_params.extended_api">
                  <toggle
                    v-model="model.email_gateway_params.extended_api"
                    :text="$t('EmailSettings_PmtaExtendedAPI')"
                  />
                </form-item>
                <form-item prop="email_gateway_params.verify">
                  <toggle
                    v-model="model.email_gateway_params.verify"
                    :text="$t('EmailSettings_PmtaVerify')"
                  />
                </form-item>
                <form-item prop="email_gateway_params.cloudhosted">
                  <toggle
                    v-model="model.email_gateway_params.cloudhosted"
                    :text="$t('EmailSettings_PmtaCloudhosted')"
                  />
                </form-item>
                <form-item prop="email_gateway_params.compression">
                  <toggle
                    v-model="model.email_gateway_params.compression"
                    :text="$t('EmailSettings_PmtaCompression')"
                  />
                </form-item>
                <form-item prop="email_gateway_params.async">
                  <toggle
                    v-model="model.email_gateway_params.async"
                    :text="$t('EmailSettings_PmtaAsync')"
                  />
                </form-item>
                <form-item
                  prop="email_gateway_params.key"
                  :label="$t('EmailSettings_PmtaKey')"
                >
                  <el-input v-model="model.email_gateway_params.key" />
                </form-item>
                <form-item
                  prop="email_gateway_params.version"
                  :label="$t('EmailSettings_PmtaVersion')"
                >
                  <el-input v-model="model.email_gateway_params.version" />
                </form-item>
              </el-col>
            </el-row>
          </el-form>
        </form-section>
      </rest-form>
      <rest-page
        v-if="isEmailSettingsVisible"
        :fetch-endpoint="fetchOtherSettingsEndpoint"
      >
        <rest-form
          ref="other-settings-form"
          :save-endpoint="saveEndpoint"
          :model="otherSettingsModel"
          :disabled="isDisabled"
        >
          <form-section>
            <el-form :model="otherSettingsModel" :disabled="isDisabled">
              <form-section-header
                :title="$t('EmailSettings_Other_Settings_Title')"
              >
                <template slot="controls">
                  <save-button
                    :click="() => $refs['other-settings-form'].save()"
                  />
                </template>
              </form-section-header>
              <el-row>
                <el-col :span="12">
                  <form-item
                    class="advanced-bot-filter"
                    :label="$t('EmailSettings_Advanced_Bot_Detection_Title')"
                    :description="
                      $t('EmailSettings_Advanced_Bot_Detection_Description')
                    "
                  >
                    <toggle
                      v-model="otherSettingsModel.advanced_email_bot_filter"
                      :disabled="isEmailSettingsDisabled"
                    />
                  </form-item>
                </el-col>
                <el-col v-if="isPmtaGateway" :span="12">
                  <form-item
                    class="email-delivery-receipts"
                    :label="$t('EmailSettings_DeliveryReceipts_Title')"
                    :description="$t('EmailSettings_DeliveryReceipts_Desc')"
                  >
                    <toggle
                      v-model="otherSettingsModel.email_delivery_receipts"
                      :disabled="isEmailSettingsDisabled"
                    />
                  </form-item>
                </el-col>
              </el-row>
            </el-form>
          </form-section>
        </rest-form>
      </rest-page>
    </page-content>
  </rest-page>
</template>

<script>
import UnsubscribePreview from '@/modules/settings/components/UnsubscribePreview'
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import ColorPickerField from '@/common/components/form/ColorPickerField'
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import Toggle from '@/common/components/form/Toggle'
import Radio from '@/common/components/form/Radio'
import RadioGroup from '@/common/components/form/RadioGroup'
import FormSectionHeader from '@/common/components/form/FormSectionHeader'
import RestFormItem from '@/common/components/rest/RestFormItem'
import IconUpload from '@/common/components/form/IconUpload'
import SaveButton from '@/common/components/form/SaveButton'
import RestApi from '@/core/services/RestApi'
import { emailVerificationEnum } from '@/modules/settings/enum/EmailVerificationEnum'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'

export default {
  name: 'EmailSettingsPage',
  components: {
    PageContent,
    PageHeaderNew,
    SaveButton,
    IconUpload,
    RestFormItem,
    FormSectionHeader,
    RadioGroup,
    Radio,
    Toggle,
    FormItem,
    FormSection,
    ColorPickerField,
    RestForm,
    RestPage,
    UnsubscribePreview
  },
  data() {
    return {
      readonly: false,
      projectId: null,
      verifyLoading: false,
      model: {
        email_subscription_page: {
          logo: '',
          background: '',
          email_change_enabled: 0
        },
        email_verification: 0,
        email_gateway_params: {
          host: null,
          port: null,
          baseuri: null,
          extended_api: 1,
          verify: 1,
          cloudhosted: 1,
          compression: 1,
          async: 0,
          key: null,
          version: 'v1'
        }
      },
      otherSettingsModel: {
        advanced_email_bot_filter: null,
        email_delivery_receipts: 1
      },
      emailVerificationEnum
    }
  },
  methods: {
    save() {
      // Only validate the settings form if it exists (when email settings are visible)
      if (this.$refs['settings']) {
        this.$refs['settings'].validate(valid => {
          if (valid) {
            this.$refs['form'].save()
          }
        })
      } else {
        // If email settings section isn't visible, just save the form directly
        this.$refs['form'].save()
      }
    },
    afterSave(data, finishLoading) {
      finishLoading.apply()
    },
    onManagePreferencesChange(enabled) {
      if (
        !enabled &&
        !this.model.subscription_preferences_global_unsubscribe_from_all_enabled
      ) {
        this.model.subscription_preferences_global_unsubscribe_from_all_enabled = 1
      }
    },
    emailVerify() {
      this.$msgbox({
        title: this.$t('EmailSettings_VerificationDialog_Title'),
        message: this.$t('EmailSettings_VerificationDialog_Prompt'),
        showCancelButton: true,
        confirmButtonText: this.$t(
          'EmailSettings_VerificationDialog_Button_Confirm'
        ),
        cancelButtonText: this.$t(
          'EmailSettings_VerificationDialog_Button_Cancel'
        ),
        center: true
      }).then(() => {
        this.verifyLoading = true
        RestApi(
          projectEndpoint({
            method: 'post',
            url: `email-verify`
          })
        )
          .then(() => {
            this.$message({
              message: this.$t('EmailSettings_EmailVerified'),
              type: 'success'
            })
            this.model.email_verification = this.emailVerificationEnum.VERIFIED
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
          .finally(() => {
            this.verifyLoading = false
          })
      })
    }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          label: this.$t('Settings_Breadcrumb_Settings')
        },
        {
          label: this.$t('Settings_Breadcrumb_Channels'),
          route: projectRoute({
            name: 'EnableChannelsTab'
          })
        },
        {
          label: this.$t('Channels_EmailSettings')
        }
      ]
    },
    fetchEndpoint() {
      return projectEndpoint(
        {
          method: 'get',
          url: ``,
          params: {
            expand:
              'email_type,email_mta_queue,email_return_domain,email_vanity_domain,email_subscription_page,email_gateway,email_verification,email_gateway_params,subscription_preferences_global_unsubscribe_from_all_enabled'
          },
          handler: data => {
            this.model = {
              email_subscription_page: {
                ...data.email_subscription_page
              },
              email_mta_queue: data.email_mta_queue,
              email_return_domain: data.email_return_domain,
              email_vanity_domain: data.email_vanity_domain,
              email_type: data.email_type,
              email_gateway: data.email_gateway,
              email_verification: data.email_verification,
              email_gateway_params:
                data.email_gateway_params ||
                this.model.email_gateway_params ||
                {},
              subscription_preferences_global_unsubscribe_from_all_enabled:
                data.subscription_preferences_global_unsubscribe_from_all_enabled
            }
          }
        },
        this.projectId || this.$route.query.project_id
      )
    },
    fetchOtherSettingsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: ``,
        params: {
          expand: 'advanced_email_bot_filter,email_delivery_receipts'
        },
        handler: data => {
          this.otherSettingsModel = {
            advanced_email_bot_filter: data.advanced_email_bot_filter,
            email_delivery_receipts: data.email_delivery_receipts
          }
        }
      })
    },

    saveEndpoint() {
      return projectEndpoint(
        {
          method: 'patch',
          url: ''
        },
        this.projectId || this.$route.query.project_id
      )
    },
    logo() {
      if (this.model.email_subscription_logo_file) {
        return this.model.email_subscription_logo_file
      } else {
        return this.model.email_subscription_page.logo
      }
    },
    isEmailSettingsVisible() {
      return this.$can('XpEmailConfiguration:ReadAccess')
    },
    isEmailSettingsDisabled() {
      return !this.$can('XpEmailConfiguration:FullAccess')
    },
    isPmtaGateway() {
      return this.model.email_gateway === 'pmta'
    },
    isSubscriptionPreferencesEnabled() {
      return this.$store.state.app.features.subscriptionPreferences
    },
    // TODO(XP-12684): remove after rollout
    isReleaseSubscriptionPreferencesDisableGlobalUnsubscribeEnabled() {
      return !!this.$store.state.app.features
        .releaseSubscriptionPreferencesDisableGlobalUnsubscribe
    },
    isDisabled() {
      return !this.$can('updateProject')
    },
    needVerification() {
      return (
        this.model.email_verification !== this.emailVerificationEnum.VERIFIED
      )
    },
    validationRules() {
      if (this.model.email_gateway !== 'pmtaapi') return {}

      return {
        email_gateway_params: {
          host: {
            type: 'string',
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          port: [
            {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            },
            {
              type: 'number',
              message: this.$t('RestForm_FieldShouldBeANumber')
            }
          ]
        }
      }
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/settings/pages/EmailSettingsPage.vue